import { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Alert, Typography, Paper, Container } from '@mui/material';
import { format } from 'date-fns';
import {
    useNavigate,
    useLocation,
    Navigate,
    useSearchParams,
} from 'react-router-dom';
import {
    AppContext,
    SttTimetable,
    SttTimetableWeek,
    SttDialog,
    SttPage,
} from '../../../sporttia/all';
import BookingDialog from '../../../components/dialogs/bookingDialog/BookingDialog';
import TimetableControls from './TimetableControls';
import TimetableOperate from './TimetableOperate';
import { getPath } from '../../Pages';
import BusyScheduleDialog from '../../../components/dialogs/BusyScheduleDialog';
import WidgetLogin from '../widget/components/WidgetLogin';
import { getErrorMessage, redirectLogin } from '../../../lib/utils';
import BookingsConsentsPopUp from '../../../components/popups/BookingsConsentsPopUp';
import ActivityClassDialog from '../../sc/activities/ActivityClassDialog';
import Activity from '../../sc/activities/Activity';
import { useLoader } from '../../../lib/hooks';
import { useTimetableTour } from 'src/lib/tours/useTimetableTour';

export default function Timetable() {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const { startTour } = useTimetableTour();

    // Set page header title
    useEffect(() => {
        cxt.setHeader({ title: t('timetable.title') });
    }, []);

    // Get SC ID from URL query params or context
    const id = useMemo(() => {
        const idFromQuery = searchParams.get('idSC');
        return idFromQuery ? Number(idFromQuery) : cxt.sc?.id;
    }, [searchParams, cxt.sc?.id]);

    // Initialize date from URL or use today
    const [date, setDate] = useState(() => {
        const dateParam = searchParams.get('date');
        return dateParam || format(new Date(), 'yyyy-MM-dd');
    });
    const [timetable, setTimetable] = useState<any>();
    const [selectedPiece, setSelectedPiece] = useState<any>(null);
    const [selectedColumn, setSelectedColumn] = useState<any>(null);
    const [isWeekly, setIsWeekly] = useState(false);
    const [openBookingId, setOpenBookingId] = useState<number | null>(null);
    const [loginDialog, setLoginDialog] = useState<boolean | null>(null);
    const [openLesson, setOpenLesson] = useState<any>(null);
    const [_affidavitAccepted, setAffidavitAccepted] = useState<boolean | null>(
        null,
    );

    // Copy / paste booking id
    const [bookingCut, setBookingCut] = useState<any>(null);

    // ver busy
    const [busySchedule, setBusySchedule] = useState<any>(null);

    // Top control configuration (persisted in localStorage)
    const [columnWidth, setColumnWidth] = useState(() => {
        const saved = localStorage.getItem('timetable_columnWidth');
        return saved ? Number(saved) : 100;
    });
    // Initialize facilityGroupId from URL or undefined
    const [facilityGroupId, setFacilityGroupId] = useState<number | undefined>(
        () => {
            const groupParam = searchParams.get('idFieldGroup');
            return groupParam ? Number(groupParam) : undefined;
        },
    );

    // Privilegios para ver los alquileres
    const [ImUserPrivileged, setImUserPrivileged] = useState(false);

    // Para ver actividades
    const [showActivity, setShowActivity] = useState(false);
    const [loading, setLoading] = useState('idle');

    // Column width coeffs - memoized to avoid recreation on each render
    const coeffs = useMemo(
        () => [
            {
                id: 'NORMAL',
                width: 100,
                name: t('timetable.widthNormal'),
            },
            {
                id: 'CLOSE',
                width: 50,
                name: t('timetable.widthClose'),
            },
            {
                id: 'WIDE',
                width: 150,
                name: t('timetable.widthWide'),
            },
            {
                id: 'VERY_WIDE',
                width: 200,
                name: t('timetable.widthVeryWide'),
            },
        ],
        [t],
    );

    /**
     * Load timetable
     */
    const loadTimetable = useCallback(() => {
        if (!id) return; // Guard against undefined id

        setLoading('loading');
        cxt.api('GET', '/timetable', {
            params: {
                idSC: id,
                date,
                idFieldGroup:
                    facilityGroupId && facilityGroupId > 0
                        ? facilityGroupId
                        : null,
                weekly: isWeekly,
            },
            success: (r: any) => {
                const data = r.one || r;
                setTimetable(data);

                // Auto-adjust column width only if user hasn't manually selected one
                if (!localStorage.getItem('timetable_columnWidth')) {
                    const count = data?.count || 0;
                    if (count < 5) {
                        setColumnWidth(200); // VERY_WIDE
                    } else if (count < 7) {
                        setColumnWidth(150); // WIDE
                    } else {
                        setColumnWidth(100); // NORMAL
                    }
                }

                setLoading('idle');
            },
            error: (r: any) => {
                setLoading('idle');
                cxt.showMessage('E', getErrorMessage(r));
            },
        });
    }, [id, date, facilityGroupId, isWeekly, cxt]);

    /**
     * Update URL query params
     */
    const updateUrlParams = useCallback(
        (newDate?: string, newFacilityGroupId?: number) => {
            const params = new URLSearchParams(searchParams);

            // Update date param
            if (newDate) {
                params.set('date', newDate);
            }

            // Update facility group param
            if (newFacilityGroupId !== undefined) {
                if (newFacilityGroupId === -1) {
                    params.delete('idFieldGroup'); // Remove param for "all"
                } else {
                    params.set('idFieldGroup', String(newFacilityGroupId));
                }
            }

            // Keep idSC if it exists
            const idSC = searchParams.get('idSC');
            if (idSC) {
                params.set('idSC', idSC);
            }

            setSearchParams(params, { replace: true });
        },
        [searchParams, setSearchParams],
    );

    /**
     * On change any control
     */
    const onChange = useCallback(
        (p: { date?: string; coeff?: any; facilityGroupId?: any } = {}) => {
            // Date changes
            if (p.date) {
                setDate(p.date);
                updateUrlParams(p.date, undefined);
            }

            // Facility group changes
            if (p.facilityGroupId !== undefined) {
                setFacilityGroupId(p.facilityGroupId);
                updateUrlParams(undefined, p.facilityGroupId);
            }

            // Coeff changes
            if (p.coeff) {
                const coeff = coeffs.find((e: any) => e.id === p.coeff);
                if (coeff) {
                    setColumnWidth(coeff.width);
                    localStorage.setItem(
                        'timetable_columnWidth',
                        String(coeff.width),
                    );
                }
            }
        },
        [coeffs, updateUrlParams],
    );

    /**
     * On change timetable
     */
    const onChangeTimetable = useCallback(() => {
        // Close all
        setSelectedPiece(null);

        // Refresh
        loadTimetable();
    }, [loadTimetable]);

    /**
     * Top controls change - Load timetable when dependencies change
     */
    useEffect(() => {
        if (id && facilityGroupId !== undefined) {
            loadTimetable();
        }
    }, [id, facilityGroupId, date, isWeekly]);

    /**
     * Sync state with URL params (for browser back/forward navigation)
     */
    useEffect(() => {
        const dateParam = searchParams.get('date');
        const groupParam = searchParams.get('idFieldGroup');

        if (dateParam && dateParam !== date) {
            setDate(dateParam);
        }
        if (groupParam) {
            const groupId = Number(groupParam);
            if (groupId !== facilityGroupId) {
                setFacilityGroupId(groupId);
            }
        }
    }, [searchParams]);

    /**
     * Check if user have "19" privilege (can see bookings people names).
     */
    const userId = cxt.user?.id;
    const userRole = cxt.user?.role;
    const api = cxt.api;

    useEffect(() => {
        if (id && userId && userRole === 'USER') {
            api('GET', `/scs/${id}/me`, {
                success: (result: any) => {
                    if (result.mship && result.mship.privileges.includes(19)) {
                        setImUserPrivileged(true);
                    }
                },
            });
        }
    }, [userId, userRole, id, api]);

    /**
     * Open piece
     */
    const openPiece = useCallback(
        (column: any, piece: any) => {
            setSelectedColumn(column);

            // If I'm user I only can use a FREE piece or see my own booking
            if (
                cxt.user?.role === cxt.constants.roleUser ||
                cxt.user?.role === cxt.constants.roleTeacher
            ) {
                switch (piece.mark) {
                    case 'FREE':
                        setSelectedPiece(piece);
                        break;

                    case 'FULL':
                        if (ImUserPrivileged) {
                            setSelectedPiece(piece);
                        }
                        break;

                    case 'RENT':
                        if (piece.isGame) {
                            if (piece.isGamePublic || ImUserPrivileged) {
                                navigate(
                                    getPath('game', {
                                        state: {
                                            id: piece.bookings?.[0]?.id,
                                        },
                                    }),
                                );
                            } else if (
                                piece.players.some(
                                    (player: any) => player.id === cxt.user?.id,
                                )
                            ) {
                                navigate(
                                    `${getPath('game', {
                                        state: {
                                            id: piece.bookings?.[0]?.id,
                                        },
                                    })}?code=${piece.bookings?.[0]?.signupCode}`,
                                );
                            }
                        } else if (
                            piece.bookings?.[0]?.user &&
                            piece.bookings[0].user.id === cxt.user?.id
                        ) {
                            navigate(
                                getPath('booked', {
                                    state: {
                                        id: piece.bookings[0].id,
                                    },
                                }),
                            );
                        }
                        break;

                    case 'EVENT':
                        navigate(
                            getPath('eventPublic', {
                                state: {
                                    id: piece?.bookings?.[0]?.event_id,
                                },
                            }),
                        );
                        break;

                    case 'CLASS':
                        setOpenLesson(piece?.bookings?.[0]);
                        break;

                    default:
                        break;
                }
            } else if (cxt.user?.role === cxt.constants.roleSC) {
                // I'm SC owner
                switch (piece.mark) {
                    case 'BUSY':
                        setBusySchedule({
                            id: piece.bookings?.[0]?.id,
                            idFacility: column.facility
                                ? column.facility.id
                                : undefined,
                            name: piece.title,
                            notes: piece.notes,
                            ini: piece.ini,
                            end: piece.end,
                            timeini: format(new Date(piece.ini), 'HH:mm'),
                            timeend: format(new Date(piece.end), 'HH:mm'),
                        });
                        break;

                    case 'RENT':
                        if (piece.isGame && piece.bookings?.[0]?.id) {
                            navigate(
                                getPath('scGame', {
                                    id: piece.bookings[0].id,
                                }),
                                { state: { capacity: piece.capacity?.total } },
                            );
                        } else if (piece.bookings?.[0]?.id) {
                            setOpenBookingId(piece.bookings[0].id);
                        }
                        break;

                    case 'CLASS':
                        setOpenLesson(piece?.bookings?.[0]);
                        break;

                    default:
                        if (piece.isGame && piece.bookings?.[0]?.id) {
                            navigate(
                                getPath('scGame', {
                                    id: piece.bookings[0].id,
                                }),
                                { state: { capacity: piece.capacity?.total } },
                            );
                        } else {
                            setSelectedPiece(piece);
                            setSelectedColumn(column);
                        }
                        break;
                }
            } else if (piece.mark === 'FREE') {
                // I'm not logged in
                redirectLogin(navigate, location);
            }
        },
        [cxt, navigate, location, ImUserPrivileged],
    );

    /**
     * Cut booking
     */
    const cutBooking = useCallback(
        (booking: any) => {
            setBookingCut(booking);
            setSelectedPiece(null);
            setOpenBookingId(null);
            cxt.showMessage('S', t('timetable.copyPasteBookingPrepared'));
        },
        [cxt, t],
    );

    /**
     * Toggle weekly
     */
    const toggleWeekly = useCallback(() => {
        setIsWeekly((prev) => !prev);
    }, []);

    const [, loader] = useLoader([loading]);

    // Redirect to login if no user is logged in and no SC ID is available
    if (!id && !cxt.logged) {
        return (
            <Navigate
                to={getPath('login')}
                state={{ from: location }}
                replace
            />
        );
    }

    // Show message when logged in but no sports center is selected
    if (!id) {
        return (
            <SttPage>
                <Container maxWidth="sm">
                    <Paper className="p-8 mt-8 text-center">
                        <Typography variant="h6" className="mb-4">
                            {t('timetable.noSportsCenterSelected')}
                        </Typography>
                        <Typography color="textSecondary">
                            {t('timetable.selectSportsCenterMessage')}
                        </Typography>
                    </Paper>
                </Container>
            </SttPage>
        );
    }

    // Main render
    return (
        <SttPage help={startTour}>
            {loader}

            <TimetableControls
                sc={{ id }}
                date={date}
                loadTimetable={loadTimetable}
                onChange={onChange}
                facilityGroupId={facilityGroupId}
                coeffs={coeffs}
                isWeekly={isWeekly}
                toggleWeekly={toggleWeekly}
            />

            {/* Timetable */}
            {timetable && (
                <>
                    <Box className="mt-4">
                        {timetable.error ? (
                            <Alert severity="error">
                                {timetable.error.msg}
                            </Alert>
                        ) : (
                            <>
                                {isWeekly ? (
                                    <SttTimetableWeek
                                        columnWidth={columnWidth}
                                        weekTimetable={timetable.columns}
                                        onSelectPiece={openPiece}
                                    />
                                ) : (
                                    <SttTimetable
                                        columnWidth={columnWidth}
                                        timetable={timetable}
                                        onSelectPiece={openPiece}
                                    />
                                )}
                            </>
                        )}
                    </Box>

                    {/* Timetable infos */}
                    {timetable.infos && timetable.infos.count > 0 && (
                        <Box className="mb-4">
                            <Alert severity="info">
                                {timetable.infos.rows
                                    .map((info: any) => info.msg)
                                    .join('\n')}
                            </Alert>
                        </Box>
                    )}
                </>
            )}

            {/* Operate Dialog */}
            {selectedPiece && (
                <TimetableOperate
                    sc={{ id }}
                    selectedPiece={selectedPiece}
                    column={selectedColumn}
                    onClose={() => setSelectedPiece(null)}
                    onChangeTimetable={onChangeTimetable}
                    privileged={ImUserPrivileged}
                    bookingCut={bookingCut}
                    setBookingCut={setBookingCut}
                    cutBooking={cutBooking}
                />
            )}

            {/* Booking Dialog (SC) */}
            {openBookingId && (
                <BookingDialog
                    idBooking={openBookingId}
                    onClose={() => setOpenBookingId(null)}
                    cutBooking={cutBooking}
                    onUpdate={onChangeTimetable}
                />
            )}

            {/* Busy schedule dialog */}
            {busySchedule && (
                <BusyScheduleDialog
                    item={busySchedule}
                    onClose={() => setBusySchedule(null)}
                    onSave={onChangeTimetable}
                />
            )}

            {/* Login dialog (widget) */}
            <SttDialog
                open={!!loginDialog}
                title={t('general.login')}
                onClose={() => setLoginDialog(null)}
                content={<WidgetLogin />}
            />

            {/* Activity class dialog */}
            <ActivityClassDialog
                open={!!openLesson}
                item={openLesson}
                onClose={() => setOpenLesson(null)}
                onSave={onChangeTimetable}
                onGoToActivity={() => {
                    setOpenLesson(null);
                    setShowActivity(true);
                }}
            />

            {/* Activity full dialog */}
            <SttDialog
                open={showActivity}
                maxWidth="xl"
                title={t('general.Activity')}
                onClose={() => setShowActivity(false)}
                content={
                    openLesson && (
                        <Activity
                            id={openLesson.activity?.id}
                            trash={false}
                            onChange={() => {}}
                            onClose={() => setShowActivity(false)}
                        />
                    )
                }
            />

            {/* Booking consents popup */}
            {id && (
                <BookingsConsentsPopUp
                    scId={id}
                    onResponse={(accepted) => {
                        setAffidavitAccepted(accepted);
                    }}
                />
            )}
        </SttPage>
    );
}
